import type { PayrollCalculatorEditableRow } from '@/lib/api/accounting';
import type { EmployeePayrollSummary, PayrollProjectGroup } from '@/lib/payrollCalculator';
import { getAccountingPrintSettings } from '@/lib/printSettings';

const FONT = 'Tahoma';
const NUM_FMT = '#,##0';

type ExportPayrollExcelInput = {
  month: string;
  month_label: string;
  date_range_label?: string;
  projectGroups: PayrollProjectGroup[];
  employeeSummary: EmployeePayrollSummary[];
  detailRows: PayrollCalculatorEditableRow[];
  totalNet: number;
};

function todayFaLabel(): string {
  try {
    return new Intl.DateTimeFormat('fa-IR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date());
  } catch {
    return new Date().toLocaleString('fa-IR');
  }
}

function setFill(cell: { fill?: unknown }, argb: string) {
  cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb } };
}

function thinBorder() {
  return {
    top: { style: 'thin' as const },
    left: { style: 'thin' as const },
    bottom: { style: 'thin' as const },
    right: { style: 'thin' as const },
  };
}

function styleHeaderRow(row: { eachCell: (cb: (c: { font?: unknown; fill?: unknown; alignment?: unknown; border?: unknown }) => void) => void }) {
  row.eachCell((cell) => {
    cell.font = { name: FONT, size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
    setFill(cell, 'FF1565C0');
    cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
    cell.border = thinBorder();
  });
}

function addTitleBlock(
  ws: { mergeCells: (a: number, b: number, c: number, d: number) => void; getCell: (r: number, c: number) => { value?: unknown; font?: unknown; fill?: unknown; alignment?: unknown }; getRow: (r: number) => { height?: number } },
  title: string,
  subtitle: string,
  colCount: number,
): number {
  let row = 1;
  ws.mergeCells(row, 1, row, colCount);
  const t = ws.getCell(row, 1);
  t.value = title;
  t.font = { name: FONT, size: 16, bold: true, color: { argb: 'FFFFFFFF' } };
  setFill(t, 'FF0F4C75');
  t.alignment = { horizontal: 'center', vertical: 'middle' };
  ws.getRow(row).height = 28;
  row += 1;

  ws.mergeCells(row, 1, row, colCount);
  const s = ws.getCell(row, 1);
  s.value = subtitle;
  s.font = { name: FONT, size: 11, bold: true };
  setFill(s, 'FFE3F2FD');
  s.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
  row += 1;

  ws.mergeCells(row, 1, row, colCount);
  const m = ws.getCell(row, 1);
  m.value = `تاریخ خروجی: ${todayFaLabel()}`;
  m.font = { name: FONT, size: 9, italic: true };
  setFill(m, 'FFF5F5F5');
  m.alignment = { horizontal: 'center', vertical: 'middle' };
  row += 2;

  return row;
}

export async function exportPayrollExcel(input: ExportPayrollExcelInput): Promise<void> {
  const companyName = getAccountingPrintSettings().companyName;
  const ExcelJS = (await import('exceljs')).default;
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Darvag Accounting';
  wb.created = new Date();

  const fileLabel = input.month_label.replace(/\//g, '-').replace(/\s+/g, '_');

  // ——— خلاصه ———
  {
    const ws = wb.addWorksheet('خلاصه', { views: [{ rightToLeft: true }] });
    let row = addTitleBlock(
      ws,
      companyName,
      `گزارش محاسبه حقوق — ${input.month_label}`,
      4,
    );
    const summary = [
      ['ماه', input.month_label],
      ['بازه', input.date_range_label || '—'],
      ['تعداد پروژه', input.projectGroups.length],
      ['تعداد پرسنل', input.employeeSummary.length],
      ['تعداد ردیف جزئیات', input.detailRows.length],
      ['جمع خالص پرداخت', input.totalNet],
    ];
    for (const [label, val] of summary) {
      ws.getCell(row, 1).value = label;
      ws.getCell(row, 1).font = { name: FONT, bold: true };
      const v = ws.getCell(row, 2);
      v.value = val;
      if (typeof val === 'number') v.numFmt = NUM_FMT;
      row += 1;
    }
    ws.getColumn(1).width = 22;
    ws.getColumn(2).width = 28;
  }

  // ——— جمع پرسنل ———
  {
    const ws = wb.addWorksheet('جمع پرسنل', { views: [{ rightToLeft: true }] });
    const headers = ['ردیف', 'پرسنل', 'کد پرسنل', 'پروژه‌ها', 'خالص تجمیعی', 'بانک', 'شبا'];
    const colCount = headers.length;
    let row = addTitleBlock(ws, companyName, `جمع‌بندی پرسنل — ${input.month_label}`, colCount);
    const hr = ws.getRow(row);
    headers.forEach((h, i) => {
      hr.getCell(i + 1).value = h;
    });
    styleHeaderRow(hr);
    row += 1;

    input.employeeSummary.forEach((e, index) => {
      const dataRow = ws.getRow(row);
      const projects = e.project_parts.map((p) => `${p.project_name}: ${p.net}`).join(' | ');
      [
        index + 1,
        e.employee_name,
        e.employee_code || '',
        projects,
        e.total_net,
        e.bank_name || '',
        e.iban || '',
      ].forEach((val, colIdx) => {
        const cell = dataRow.getCell(colIdx + 1);
        cell.value = val;
        cell.font = { name: FONT, size: 10 };
        cell.border = thinBorder();
        if (colIdx === 4 && typeof val === 'number') cell.numFmt = NUM_FMT;
      });
      row += 1;
    });

    [6, 22, 12, 40, 16, 16, 24].forEach((w, i) => {
      ws.getColumn(i + 1).width = w;
    });
  }

  // ——— به تفکیک پروژه ———
  const usedSheetNames = new Set<string>(['خلاصه', 'جمع پرسنل']);
  for (const group of input.projectGroups) {
    const base = group.project_name.replace(/[\\/*?:[\]]/g, '').trim().slice(0, 22) || `p${group.project_id}`;
    let sheetName = `${base}_${group.project_id}`.slice(0, 31);
    let suffix = 0;
    while (usedSheetNames.has(sheetName)) {
      suffix += 1;
      sheetName = `${base}_${group.project_id}_${suffix}`.slice(0, 31);
    }
    usedSheetNames.add(sheetName);
    const ws = wb.addWorksheet(sheetName, { views: [{ rightToLeft: true }] });
    const headers = [
      'ردیف',
      'پرسنل',
      'نوع حقوق',
      'حاضر',
      'حقوق پایه',
      'کارکرد',
      'کسر غیبت',
      'اضافه‌کار',
      'خالص',
      'فرمول/حالت',
    ];
    const colCount = headers.length;
    let row = addTitleBlock(
      ws,
      group.project_name,
      `جمع پروژه: ${group.totalNet.toLocaleString('en-US')} — ${input.month_label}`,
      colCount,
    );
    const hr = ws.getRow(row);
    headers.forEach((h, i) => {
      hr.getCell(i + 1).value = h;
    });
    styleHeaderRow(hr);
    row += 1;

    group.rows.forEach((r, index) => {
      const dataRow = ws.getRow(row);
      const mode =
        r.net_mode === 'formula' ? `فرمول: ${r.net_formula || ''}` : r.net_mode === 'manual' ? 'دستی' : 'خودکار';
      [
        index + 1,
        r.employee_name,
        r.salary_type_label,
        r.present_days,
        r.base_salary,
        r.worked_salary,
        r.absence_deduction,
        r.overtime_hours,
        r.computed_net,
        mode,
      ].forEach((val, colIdx) => {
        const cell = dataRow.getCell(colIdx + 1);
        cell.value = val;
        cell.font = { name: FONT, size: 10 };
        cell.border = thinBorder();
        if ([4, 5, 6, 8].includes(colIdx) && typeof val === 'number') cell.numFmt = NUM_FMT;
      });
      row += 1;
    });

    [5, 20, 10, 8, 14, 14, 12, 10, 14, 24].forEach((w, i) => {
      ws.getColumn(i + 1).width = w;
    });
  }

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `payroll-${fileLabel}.xlsx`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
